import { useState } from 'react';
import Navbar from './components/Navbar';
import StatsCards from './components/StatsCards';
import QuickActions from './components/QuickActions';
import OrderTrends from './components/OrderTrends';
import OrderDistribution from './components/OrderDistribution';
import Calendar from './components/Calendar';
import RecentOrders from './components/RecentOrders';
import RejectedOrdersNotice from './components/RejectedOrdersNotice';
import TodayReminders from './components/TodayReminders';
import ArrivalReminders from './components/ArrivalReminders';
import OrderFormModal from '../orders/components/OrderFormModal';
import { SiteFiling } from '../../components/common/SiteFiling';
import { useHomeStats } from './hooks/useHomeStats';

export default function Home() {
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const { data: homeStats, loading: statsLoading } = useHomeStats();

  return (
    <div className="min-h-screen bg-[#f8fafa]">
      <Navbar />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 md:py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-5 md:mb-8">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">仪表盘</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreateOrder(true)}
              className="px-3 md:px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="hidden sm:inline">创建订单</span>
              <span className="sm:hidden"><i className="ri-add-line" /></span>
            </button>
            <button className="px-3 py-2 border border-gray-200 bg-white text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-filter-3-line text-sm" />
              <span className="hidden sm:inline">筛选</span>
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <QuickActions />

        {/* 被对方驳回、待我修改重提：常驻一行 + 登录进首页弹一次清单 */}
        <RejectedOrdersNotice />

        {/* Today Reminders */}
        <TodayReminders />

        {/* Arrival Reminders */}
        <div className="mb-4 md:mb-5">
          <ArrivalReminders />
        </div>

        {/* Stats Cards */}
        <StatsCards />

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-5 mb-4 md:mb-6">
          <OrderTrends />
          <OrderDistribution />
        </div>

        {/* Calendar and Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-5 mb-4 md:mb-6">
          <div className="lg:col-span-2">
            <Calendar />
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-5 h-full">
              <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-4">快速统计</h3>
              <div className="space-y-3">
                {
                  [
                    {
                      label: '未执行订单',
                      value: statsLoading ? '-' : String(homeStats?.pending ?? 0),
                      icon: 'ri-time-line',
                      bg: 'bg-amber-50',
                      iconColor: 'text-amber-500',
                    },
                    {
                      label: '已完成订单',
                      value: statsLoading ? '-' : String(homeStats?.completed ?? 0),
                      icon: 'ri-check-double-line',
                      bg: 'bg-teal-50',
                      iconColor: 'text-teal-500',
                    },
                    {
                      label: '执行中订单',
                      value: statsLoading ? '-' : String(homeStats?.inProgress ?? 0),
                      icon: 'ri-loader-4-line',
                      bg: 'bg-cyan-50',
                      iconColor: 'text-cyan-500',
                    },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 p-3 md:p-4 bg-gray-50 rounded-xl">
                      <div className={`w-9 h-9 md:w-10 md:h-10 ${item.bg} rounded-xl flex items-center justify-center shrink-0`}>
                        <i className={`${item.icon} ${item.iconColor} text-base md:text-lg`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{item.label}</p>
                        <p className={`text-lg md:text-xl font-bold text-gray-900 ${statsLoading ? 'animate-pulse' : ''}`}>
                          {item.value}
                        </p>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <RecentOrders />

        {/* 备案信息：首页底部必须常驻（见 SiteFiling 说明） */}
        <footer className="mt-6 md:mt-8 pt-5 border-t border-gray-200 text-center text-xs text-slate-500">
          <SiteFiling />
          <nav className="mt-3 flex items-center justify-center gap-4">
            <a href="/terms" className="opacity-70 hover:opacity-100 transition-opacity">
              服务条款
            </a>
            <a href="/privacy" className="opacity-70 hover:opacity-100 transition-opacity">
              隐私政策
            </a>
            <a href="/contact" className="opacity-70 hover:opacity-100 transition-opacity">
              联系我们
            </a>
          </nav>
        </footer>
      </main>

      {showCreateOrder && (
        <OrderFormModal
          mode="create"
          onClose={() => setShowCreateOrder(false)}
          onSaved={() => setShowCreateOrder(false)}
        />
      )}
    </div>
  );
}